import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';

const navLinks = [
  { label: 'Residences', href: '/residences' },
  { label: 'Interactive Map', href: '/site-plan' },
  { label: 'Amenities', href: '/amenities' },
  { label: 'Commercial', href: '/commercial' },
  { label: 'Invest', href: '/invest' },
  { label: 'About', href: '/about' },
];

interface NavBarProps {
  onRegisterClick: () => void;
  onConsultClick: () => void;
  forceDarkText?: boolean;
}

export const NavBar = ({ onRegisterClick, onConsultClick, forceDarkText = false }: NavBarProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, label: string) => {
    e.preventDefault();
    trackEvent('cta_click', { location: 'nav', target: label });
    setMobileOpen(false);
    
    // Handle different navigation types
    if (href.startsWith('/')) {
      // Direct route navigation using React Router
      navigate(href);
    } else if (location.pathname !== '/') {
      // Anchor navigation from other pages
      navigate(`/${href}`);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-24">
          {/* Logo */}
          <a 
            href="/" 
            className="flex items-center"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
          >
            <img
              src={(forceDarkText || isScrolled) ? '/logo-blk-x-gh.png' : '/logo-wh-x-gh.png'}
              alt="The Pearl at Grand Harbour"
              className="h-10 lg:h-16 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href, link.label)}
                className={`text-sm font-medium transition-colors duration-300 ${
                  forceDarkText || isScrolled 
                    ? 'text-foreground/80 hover:text-brand-teal' 
                    : 'text-white/90 hover:text-brand-teal'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              className={`hover:bg-brand-teal/10 transition-colors duration-300 ${
                forceDarkText || isScrolled 
                  ? 'text-foreground/80 hover:text-brand-teal' 
                  : 'text-white/90 hover:text-brand-teal'
              }`}
              onClick={() => {
                trackEvent('cta_click', { location: 'nav', action: 'book_consult' });
                onConsultClick();
              }}
            >
              Book Consult
            </Button>
            <Button
              size="sm"
              className="hover:scale-105 transition-transform duration-300 shadow-md hover:shadow-lg"
              onClick={() => {
                trackEvent('cta_click', { location: 'nav', action: 'register' });
                onRegisterClick();
              }}
            >
              Register Interest
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              {/* Logo */}
              <div className="flex justify-center pt-6 pb-4">
                <a 
                  href="/" 
                  className="flex items-center"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/');
                    setMobileOpen(false);
                  }}
                >
                  <img
                    src="/logo-blk-x-gh.png"
                    alt="The Pearl at Grand Harbour"
                    className="h-12 w-auto object-contain"
                  />
                </a>
              </div>
              <nav className="flex flex-col space-y-6 mt-4">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href, link.label)}
                    className="text-lg font-medium text-foreground hover:text-brand-teal transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
                <div className="pt-6 space-y-3 border-t">
                  <Button
                    variant="outline"
                    className="w-full hover:bg-brand-teal hover:text-white hover:border-brand-teal transition-all duration-300"
                    onClick={() => {
                      trackEvent('cta_click', { location: 'mobile_nav', action: 'book_consult' });
                      onConsultClick();
                      setMobileOpen(false);
                    }}
                  >
                    Book 15-min Consult
                  </Button>
                  <Button
                    className="w-full hover:scale-105 transition-transform duration-300 shadow-md hover:shadow-lg"
                    onClick={() => {
                      trackEvent('cta_click', { location: 'mobile_nav', action: 'register' });
                      onRegisterClick();
                      setMobileOpen(false);
                    }}
                  >
                    Register Interest
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
